import React, { useEffect, useRef } from 'react';
import { CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Data Quality Bar Component
 * 
 * Visualizes data confidence score as a progress bar with color coding.
 * Used in Community Verified sections to show overall data reliability.
 * 
 * @param {Object} props
 * @param {number} props.score - Data confidence score (0-100)
 * @param {boolean} [props.showLabel] - Show quality label (default: true)
 * @param {boolean} [props.showPercentage] - Show percentage (default: true)
 * @param {string} [props.size] - Size variant ('sm' | 'md' | 'lg')
 * @param {string} [props.className] - Additional CSS classes
 */
export function DataQualityBar({
  score,
  showLabel = true,
  showPercentage = true,
  size = 'md',
  className,
}) {
  const barRef = useRef(null);

  // Clamp score between 0 and 100
  const clampedScore = Math.max(0, Math.min(100, score || 0));

  // Determine color based on score
  const getColorClasses = (score) => {
    if (score >= 80) {
      return {
        bar: 'bg-gradient-to-r from-green-500 to-emerald-500',
        bg: 'bg-green-100',
        text: 'text-green-700',
        label: 'Excellent',
      };
    } else if (score >= 60) {
      return {
        bar: 'bg-gradient-to-r from-blue-500 to-cyan-500',
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        label: 'Good',
      };
    } else if (score >= 40) {
      return {
        bar: 'bg-gradient-to-r from-amber-500 to-yellow-500',
        bg: 'bg-amber-100',
        text: 'text-amber-700',
        label: 'Fair',
      };
    } else {
      return {
        bar: 'bg-gradient-to-r from-orange-500 to-red-500',
        bg: 'bg-orange-100',
        text: 'text-orange-700',
        label: 'Limited',
      };
    }
  };

  const colors = getColorClasses(clampedScore);

  // Size variants
  const heights = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  // Animate bar filling on mount
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;

    bar.style.width = '0%';
    setTimeout(() => {
      bar.style.width = `${clampedScore}%`;
    }, 100);
  }, [clampedScore]);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header with label and percentage */}
      {(showLabel || showPercentage) && (
        <div
          className={cn(
            'flex items-center justify-between',
            textSizes[size],
            colors.text
          )}
        >
          {showLabel && (
            <span className="font-medium">
              Data Quality: {colors.label}
            </span>
          )}
          {showPercentage && (
            <span className="font-semibold">{clampedScore}%</span>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div
        className={cn(
          'w-full rounded-full overflow-hidden',
          colors.bg,
          heights[size]
        )}
      >
        <div
          ref={barRef}
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            colors.bar
          )}
          style={{ width: `${clampedScore}%` }}
          role="progressbar"
          aria-valuenow={clampedScore}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Data quality: ${clampedScore}%`}
        />
      </div>
    </div>
  );
}

/**
 * Compact Data Quality Indicator
 * 
 * A compact version for use on camp cards with limited space.
 * Shows a bar chart-style indicator with label.
 * 
 * @param {Object} props
 * @param {number} props.score - Data confidence score (0-100)
 */
export function CompactDataQualityIndicator({ score }) {
  const clampedScore = Math.max(0, Math.min(100, score || 0));

  const getColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-orange-600';
  };

  const getLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Limited';
  };

  const color = getColor(clampedScore);
  const label = getLabel(clampedScore);

  return (
    <div className="inline-flex items-center gap-2">
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={cn(
              'w-1.5 h-3 rounded-full bg-current transition-colors',
              i < Math.ceil(clampedScore / 20) ? color : 'text-gray-300'
            )}
          />
        ))}
      </div>
      <span className={cn('text-xs font-medium', color)}>{label}</span>
    </div>
  );
}

/**
 * Data Quality Breakdown Component
 * 
 * Shows the overall data quality score along with a breakdown
 * of which specific fields are verified.
 * 
 * @param {Object} props
 * @param {Object} props.camp - Camp data with user fields and confidence levels
 */
export function DataQualityBreakdown({ camp }) {
  if (!camp) {
    return null;
  }

  const verifiedFields = [
    {
      name: 'Cost',
      has: camp.user_cost_details,
      confidence: camp.cost_confidence,
    },
    {
      name: 'Schedule',
      has: camp.user_schedule_details || camp.user_session_start_time,
      confidence: camp.schedule_confidence,
    },
    {
      name: 'Dates',
      has: camp.user_camp_start_date,
      confidence: camp.dates_confidence,
    },
  ].filter((field) => field.has);

  return (
    <div className="space-y-3">
      <DataQualityBar score={camp.data_confidence_score || 50} />

      {verifiedFields.length > 0 && (
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-xs font-semibold text-gray-700 mb-2">
            Verified Fields
          </h4>
          <div className="flex flex-wrap gap-2">
            {verifiedFields.map((field) => (
              <Badge
                key={field.name}
                variant={
                  field.confidence === 'high'
                    ? 'success'
                    : field.confidence === 'medium'
                      ? 'info'
                      : 'warning'
                }
                size="xs"
              >
                <CheckCircle className="w-3 h-3" />
                {field.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataQualityBar;

